import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  TrendingUp, 
  Bot, 
  ArrowUpRight, 
  CheckCircle2, 
  Layers, 
  Building2, 
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ShieldAlert,
  ArrowRight,
  X
} from "lucide-react";
import AnimatedCounter from "../common/AnimatedCounter";

export default function CaseStudies() {
  const [selectedCase, setSelectedCase] = useState(null);
  const [activeCaseIndex, setActiveCaseIndex] = useState(0);

  // Lock background body scroll and listen for ESC key when modal is open
  useEffect(() => {
    if (selectedCase) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          setSelectedCase(null);
        }
      };
      window.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [selectedCase]);

  const projects = [
    {
      id: "seismic-migration",
      shortTab: "Seismic",
      title: "WordPress to Sitecore Enterprise Migration & AI-Search Lift",
      company: "Seismic Software",
      logo: "/logos/seismic.webp",
      heroImage: "/case-studies/seismic-hero.webp",
      heroImageJpg: "/case-studies/seismic-hero.jpg",
      period: "Jun 2025 - Aug 2026",
      badge: "Enterprise Migration & AEO",
      highlightMetric: "+120% AI Visibility & Dip Recovery",
      stats: [
        { label: "Recovery Time", value: "90 Days", note: "From -20% to full recovery" },
        { label: "AI Search Lift", value: "+120%", note: "Bing & GSC AI within 6 mos" },
        { label: "Leads Sourced", value: "30%", note: "Organic & Direct Pipeline" },
      ],
      summary: "Spearheaded technical SEO architecture for a massive WordPress-to-Sitecore migration for a global US B2B SaaS enterprise, rapidly turning an initial 20% traffic drop into record growth and AI search dominance.",
      challenge: "Enterprise CMS migration involving thousands of indexed URLs, complex content mapping, and initial 20% organic traffic dip caused by legacy URL redirects and indexing latency.",
      strategy: [
        "Conducted end-to-end page audits, canonical audits, and 1:1 URL redirect architecture to eliminate crawl loops.",
        "Collaborated closely with engineering and CRO teams to analyze user journeys and optimize high-value conversion pages.",
        "Implemented targeted Answer Engine Optimization (AEO) strategies to position Seismic content inside Bing Webmaster Tools & Google AI Overviews.",
        "Created Hybrid Engine Optimization (HEO) framework to capture both standard query intent and AI answer citations."
      ],
      tools: ["Sitecore", "WordPress", "Bing Webmaster Tools", "Google Search Console", "Screaming Frog", "GA4"]
    },
    {
      id: "myoperator-growth",
      shortTab: "MyOperator",
      title: "Scaling Inbound Revenue: 200% Organic Surge & 60% Pipeline",
      company: "MyOperator",
      logo: "/logos/myoperator.webp",
      heroImage: "/case-studies/myoperator-hero.webp",
      heroImageJpg: "/case-studies/myoperator-hero.jpg",
      period: "May 2022 - May 2025",
      badge: "SaaS Organic Growth",
      highlightMetric: "200% Traffic & 60% Company Leads",
      stats: [
        { label: "Organic Traffic", value: "+200%", note: "Multi-year sustained growth" },
        { label: "Total Leads", value: "60%", note: "SEO became #1 acquisition channel" },
        { label: "Team Managed", value: "5 FTEs", note: "SEO, Content & Web Dev" },
      ],
      summary: "Engineered and executed the organic growth roadmap for MyOperator B2B SaaS, driving 200% traffic expansion and making organic search responsible for the majority of company inbound revenue.",
      challenge: "High competitor saturation in the B2B cloud communications space, necessitating precise search intent capture rather than generic high-volume traffic.",
      strategy: [
        "Analyzed historical performance data across GA4 and GSC to identify high-converting, low-competition topic clusters.",
        "Formulated keyword-driven content architectures aligned tightly with bottom-of-funnel buyer intent.",
        "Began early experimentation with LLM search visibility to ensure brand representation in emergent AI generative responses.",
        "Led a multidisciplinary squad of 5 professionals (SEO, content writers, web developers), establishing clear KPI accountability."
      ],
      tools: ["GA4", "Google Search Console", "Ahrefs", "Semrush", "Screaming Frog", "GTM"]
    },
    {
      id: "workhours-growth",
      shortTab: "Work Hours",
      title: "From SEO Traffic to Student Growth: Transforming Workhours (Trydo School)",
      company: "Workhours (Trydo School)",
      logo: "/logos/workhours.webp",
      heroImage: "/case-studies/workhours-hero.webp",
      heroImageJpg: "/case-studies/workhours-hero.jpg",
      period: "Jul 2020 - Apr 2022",
      badge: "EdTech Growth & Mentorship",
      highlightMetric: "-60% Refunds & +35% Intent Traffic",
      stats: [
        { label: "Refund Ratio", value: "-60%", note: "Dedicated student support" },
        { label: "Intent Traffic", value: "+35%", note: "Course-intent keywords" },
        { label: "Mentored", value: "100+", note: "2 live masterclasses / week" },
      ],
      summary: "Scaled course-intent organic traffic by +35%, lowered student refund ratio by 60% with dedicated doubt-clearing and mentorship, and built action-based ConvertKit lifecycle email automations.",
      challenge: "Organic traffic was skewed toward general informational searches rather than course enrollment intent, while the founder was heavily overloaded managing training, operations, content, and emails.",
      strategy: [
        "Audited technical SEO, crawlability, indexation, and rebuilt course-intent keyword architecture.",
        "Hired Content Writer and SEO Intern, establishing SOPs for autonomous organic execution.",
        "Structured 4-pillar digital marketing curriculum and led 2 live masterclasses weekly for 100+ students.",
        "Redesigned email automation around user journeys in ConvertKit, cutting student refunds by 60%."
      ],
      tools: ["ConvertKit", "Technical SEO", "Google Search Console", "GA4", "Curriculum Design", "Screaming Frog"]
    }
  ];

  const mobileSliderRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const scrollToCaseSlide = (idx) => {
    if (idx < 0 || idx >= projects.length) return;
    setActiveCaseIndex(idx);
    isProgrammaticScrollRef.current = true;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    const container = mobileSliderRef.current;
    if (container && container.children[idx]) {
      const child = container.children[idx];
      const targetScrollLeft = child.offsetLeft - (container.clientWidth - child.offsetWidth) / 2;
      
      // Temporarily disable snap during smooth scroll to prevent any snap stutter/glitch
      container.style.scrollSnapType = 'none';
      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: "smooth"
      });
    }

    scrollTimeoutRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      const container = mobileSliderRef.current;
      if (container) {
        container.style.scrollSnapType = '';
      }
    }, 600);
  };

  const handleMobileSliderScroll = () => {
    if (!mobileSliderRef.current || isProgrammaticScrollRef.current) return;
    const container = mobileSliderRef.current;
    const viewportCenter = container.scrollLeft + container.clientWidth / 2;

    let closestIdx = 0;
    let minDistance = Infinity;

    for (let i = 0; i < projects.length; i++) {
      const child = container.children[i];
      if (!child) continue;
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const dist = Math.abs(viewportCenter - childCenter);
      if (dist < minDistance) {
        minDistance = dist;
        closestIdx = i;
      }
    }

    if (closestIdx !== activeCaseIndex && closestIdx >= 0 && closestIdx < projects.length) {
      setActiveCaseIndex(closestIdx);
    }
  };

  // Re-center active card on window resize without animation
  useEffect(() => {
    const handleResize = () => {
      const container = mobileSliderRef.current;
      if (container && container.children[activeCaseIndex]) {
        const child = container.children[activeCaseIndex];
        const targetScrollLeft = child.offsetLeft - (container.clientWidth - child.offsetWidth) / 2;
        container.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: "instant"
        });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeCaseIndex]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  return (
    <section id="case-studies" className="py-4 sm:py-6 lg:py-6 scroll-mt-[72px] bg-[#070b14] relative overflow-hidden flex flex-col justify-center min-h-[calc(100vh-72px)]">
      {/* Background glow accents */}
      <div 
        className="absolute top-1/4 right-0 w-96 h-96 pointer-events-none" 
        style={{ background: "radial-gradient(circle at center, rgba(79, 70, 229, 0.10) 0%, rgba(79, 70, 229, 0) 70%)" }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10 w-full">
        
        {/* Section Header - Compact to fit within single screen */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between mb-3 sm:mb-4 gap-2"
        >
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/[0.05] border border-white/[0.08] text-[11px] font-mono uppercase tracking-widest text-[#38bdf8] mb-1 font-semibold">
              <TrendingUp className="w-3 h-3 text-[#38bdf8]" />
              Real World Results
            </div>
            <h2 className="font-heading font-black text-2xl sm:text-3xl lg:text-3xl text-white tracking-tight uppercase leading-tight">
              Projects That{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#818cf8] via-[#38bdf8] to-[#34d399]">
                Define My Journey
              </span>
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-white/60 max-w-sm font-medium leading-relaxed">
            Tangible business outcomes, verified traffic recoveries, and pipeline metrics achieved across international B2B SaaS organizations.
          </p>
        </motion.div>

        {/* UNIFIED MULTI-CARD SHOWCASE (Desktop & Mobile - All 3 cards visible) */}
        <div className="space-y-3 sm:space-y-3.5">
          {/* Quick-Switch Selector Tabs with Smooth Sliding Pill */}
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 p-1.5 rounded-2xl bg-[#090e1a] border border-white/10 max-w-lg mx-auto relative">
            {projects.map((p, idx) => {
              const isActive = activeCaseIndex === idx;
              return (
                <button
                  key={p.id}
                  onClick={() => scrollToCaseSlide(idx)}
                  className="relative flex-1 py-1.5 sm:py-2 px-3 sm:px-4 rounded-xl text-xs sm:text-sm font-mono font-bold transition-colors text-center truncate cursor-pointer z-10"
                >
                  {isActive && (
                    <motion.div
                      layoutId="caseStudiesActiveTab"
                      className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#4f46e5]/40 to-[#06b6d4]/30 border border-[#38bdf8]/40 shadow-lg"
                      transition={{ type: "spring", stiffness: 450, damping: 35 }}
                    />
                  )}
                  <span className={`relative z-10 transition-colors duration-200 ${isActive ? "text-white" : "text-white/50 hover:text-white"}`}>
                    <span className="hidden sm:inline mr-1 text-[#38bdf8]">0{idx + 1}.</span>
                    {p.shortTab}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Horizontal Multi-Card Track (All 3 cards visible with active centered) */}
          <div
            ref={mobileSliderRef}
            onScroll={handleMobileSliderScroll}
            className="flex gap-4 sm:gap-5 overflow-x-auto pb-2 no-scrollbar case-studies-track"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {projects.map((project, idx) => {
              const isActive = activeCaseIndex === idx;
              return (
                <div
                  key={project.id}
                  onClick={() => {
                    if (!isActive) scrollToCaseSlide(idx);
                  }}
                  className={`w-[85vw] max-w-[325px] sm:max-w-none sm:w-[600px] md:w-[650px] lg:w-[670px] flex-shrink-0 p-4 sm:p-5 rounded-2xl sm:rounded-3xl bg-[#0d1322] border transition-[border-color,opacity,box-shadow] duration-300 flex flex-col justify-between ${
                    isActive
                      ? "border-[#6366f1]/60 shadow-[0_10px_35px_rgba(79,70,229,0.25)] opacity-100"
                      : "border-white/[0.08] opacity-60 hover:opacity-90 cursor-pointer"
                  }`}
                >
                  <div>
                    {/* Top Metadata */}
                    <div className="flex items-center justify-between gap-2 sm:gap-4 mb-2 sm:mb-2.5">
                      <div className="flex items-center gap-2 sm:gap-2.5">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-white/[0.08] p-1 flex items-center justify-center overflow-hidden border border-white/10">
                          <img
                            src={project.logo}
                            alt={project.company}
                            loading="lazy"
                            decoding="async"
                            className="max-h-full max-w-full object-contain filter brightness-95"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        <div>
                          <h4 className="font-heading font-bold text-white text-xs sm:text-sm leading-tight">
                            {project.company}
                          </h4>
                          <span className="text-[10px] sm:text-[11px] font-mono text-white/40 block">
                            {project.period}
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] sm:text-[10px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-[#4f46e5]/15 border border-[#6366f1]/30 text-[#818cf8] uppercase tracking-wider">
                        {project.badge}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-heading font-bold text-sm sm:text-base lg:text-lg text-white tracking-tight line-clamp-2 mb-1 sm:mb-1.5 leading-snug">
                      {project.title}
                    </h3>

                    {/* Summary */}
                    <p className="text-[11px] sm:text-xs text-white/70 leading-relaxed line-clamp-2 mb-2 sm:mb-2.5">
                      {project.summary}
                    </p>

                    {/* Stats - Animated Counter spins when card is active */}
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2 p-2 sm:p-2.5 rounded-xl sm:rounded-2xl bg-[#080d18] border border-white/[0.06] mb-2 sm:mb-2.5">
                      {project.stats.map((stat, sIdx) => (
                        <div key={`${project.id}-stat-${sIdx}`} className="text-center">
                          <p className="text-xs sm:text-sm lg:text-base font-black text-white font-heading">
                            {isActive ? (
                              <AnimatedCounter
                                key={`active-counter-${project.id}-${activeCaseIndex}-${sIdx}`}
                                value={stat.value}
                                duration={2.4}
                                delay={0.12 + sIdx * 0.1}
                              />
                            ) : (
                              <span>{stat.value}</span>
                            )}
                          </p>
                          <p className="text-[8px] sm:text-[9px] font-mono uppercase tracking-wider text-white/40">{stat.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Strategy Bullets */}
                    <div className="space-y-1 mb-2 sm:mb-2.5">
                      {project.strategy.slice(0, 2).map((item, sIdx) => (
                        <div key={sIdx} className={`flex items-start gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-white/80 ${sIdx > 0 ? "hidden sm:flex" : "flex"}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-1 sm:line-clamp-none">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    {/* Tech Tools */}
                    <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-2 sm:mb-2.5 pt-2 border-t border-white/[0.06]">
                      {project.tools.map((tool, tIdx) => (
                        <span
                          key={tIdx}
                          className={`text-[9px] sm:text-[10px] font-mono px-2 sm:px-2.5 py-0.5 rounded-md bg-white/[0.04] text-white/60 ${
                            tIdx >= 3 ? "hidden sm:inline-block" : "inline-block"
                          }`}
                        >
                          {tool}
                        </span>
                      ))}
                      {project.tools.length > 3 && (
                        <span className="sm:hidden text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/[0.02] text-white/40">
                          +{project.tools.length - 3}
                        </span>
                      )}
                    </div>

                    {/* Action Trigger - Full Case Study */}
                    {project.id === "seismic-migration" ? (
                      <Link
                        to="/casestudy/seismic/"
                        className="w-full py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white font-bold text-xs sm:text-sm tracking-wider uppercase transition-all duration-200 flex items-center justify-center gap-1.5 hover:shadow-[0_4px_20px_rgba(79,70,229,0.4)]"
                      >
                        <span>Full Case Study</span>
                        <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Link>
                    ) : project.id === "myoperator-growth" ? (
                      <Link
                        to="/casestudy/myoperator/"
                        className="w-full py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white font-bold text-xs sm:text-sm tracking-wider uppercase transition-all duration-200 flex items-center justify-center gap-1.5 hover:shadow-[0_4px_20px_rgba(79,70,229,0.4)]"
                      >
                        <span>Full Case Study</span>
                        <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Link>
                    ) : (
                      <Link
                        to="/casestudy/workhours/"
                        className="w-full py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white font-bold text-xs sm:text-sm tracking-wider uppercase transition-all duration-200 flex items-center justify-center gap-1.5 hover:shadow-[0_4px_20px_rgba(79,70,229,0.4)]"
                      >
                        <span>Full Case Study</span>
                        <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Controls: Dots and Prev/Next buttons */}
          <div className="flex items-center justify-between max-w-lg mx-auto pt-0.5 px-2">
            <div className="flex items-center gap-1.5 sm:gap-2">
              {projects.map((_, i) => (
                <button
                  key={i}
                  onClick={() => scrollToCaseSlide(i)}
                  className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    activeCaseIndex === i ? "w-6 sm:w-8 bg-[#38bdf8]" : "w-1.5 sm:w-2 bg-white/20 hover:bg-white/40"
                  }`}
                  aria-label={`Go to case study ${i + 1}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => {
                  const prev = (activeCaseIndex - 1 + projects.length) % projects.length;
                  scrollToCaseSlide(prev);
                }}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-white/5 border border-white/10 hover:border-white/25 hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all cursor-pointer"
                aria-label="Previous case study"
              >
                <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
              <button
                onClick={() => {
                  const next = (activeCaseIndex + 1) % projects.length;
                  scrollToCaseSlide(next);
                }}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-white/5 border border-white/10 hover:border-white/25 hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all cursor-pointer"
                aria-label="Next case study"
              >
                <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal / Drawer for In-Depth Case Study mounted to document.body via Portal */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {selectedCase && (
            <div 
              className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-md overflow-y-auto"
              onClick={(e) => {
                if (e.target === e.currentTarget) setSelectedCase(null);
              }}
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="bg-[#0e1424] border border-white/15 rounded-3xl max-w-2xl w-full p-6 sm:p-8 max-h-[88vh] overflow-y-auto shadow-2xl relative my-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-6">
                  <div>
                    <span className="text-xs font-mono text-[#38bdf8] font-semibold uppercase">{selectedCase.company}</span>
                    <h3 className="font-heading font-black text-xl sm:text-2xl text-white mt-1">{selectedCase.title}</h3>
                  </div>
                  <button 
                    onClick={() => setSelectedCase(null)}
                    className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                    aria-label="Close modal"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6 text-left">
                  <div>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2">The Business Challenge</h4>
                    <p className="text-sm text-white/80 leading-relaxed bg-[#080d18] p-4 rounded-xl border border-white/5">
                      {selectedCase.challenge}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-[#38bdf8] mb-3">Strategy & Implementation</h4>
                    <div className="space-y-3">
                      {selectedCase.strategy.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="text-xs sm:text-sm text-white/90 leading-relaxed">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2">Key Business Outcomes</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {selectedCase.stats.map((s, idx) => (
                        <div key={`${selectedCase.id}-${idx}`} className="p-3 rounded-xl bg-[#080d18] border border-white/5 text-center">
                          <p className="text-lg font-bold text-white font-heading">
                            <AnimatedCounter value={s.value} duration={3.0} delay={0.2 + idx * 0.12} />
                          </p>
                          <p className="text-[10px] font-mono text-white/50 uppercase">{s.label}</p>
                          <p className="text-[10px] text-emerald-400 mt-1">{s.note}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
                  {selectedCase.id === "seismic-migration" ? (
                    <Link
                      to="/casestudy/seismic/"
                      onClick={() => setSelectedCase(null)}
                      className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#38bdf8] hover:text-white transition-colors"
                    >
                      <span>Open Full Standalone Case Study Page</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  ) : selectedCase.id === "myoperator-growth" ? (
                    <Link
                      to="/casestudy/myoperator/"
                      onClick={() => setSelectedCase(null)}
                      className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#38bdf8] hover:text-white transition-colors"
                    >
                      <span>Open Full Standalone Case Study Page</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <Link
                      to="/casestudy/workhours/"
                      onClick={() => setSelectedCase(null)}
                      className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#38bdf8] hover:text-white transition-colors"
                    >
                      <span>Open Full Standalone Case Study Page</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  )}
                  <button
                    onClick={() => setSelectedCase(null)}
                    className="btn-bold-primary px-5 py-2.5 rounded-xl text-white font-semibold text-xs uppercase tracking-wider cursor-pointer"
                  >
                    Close Case Study
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
}
